/**
 * leave.ts — dual-path bridge for /leave (v7.56).
 *
 * The /leave router has been live since v6.27 (read paths) + v6.30
 * (write paths). MyLeavePage and TeamLeavePage already use it via
 * inline api.X() calls. This file unifies the helpers and adds an
 * admin workspace-wide helper for the LeavesPage HR overview.
 *
 * Helpers:
 *   - fetchEmployeeLeaveRequests(empId)      single-employee list
 *   - submitLeaveRequest(payload)            POST /leave/requests
 *   - approveLeaveRequest(id, stage)         manager OR HR approve
 *   - rejectLeaveRequest(id, stage, note?)   manager OR HR reject
 *
 * The approve/reject endpoints use a `stage` field (manager / hr) to
 * gate on the right authority — same pattern the v5 page used with
 * stage-aware UI. Server enforces the actual role check.
 */

import { api, apiConfigured } from './apiClient';
import { bridgedWrite } from './apiBridge';
import { supabase } from './supabase';
import { runAutomations } from './automations';


export interface LeaveRequestRow {
  id: string;
  employee_id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  days: number;
  reason: string | null;
  status: string;
  manager_note: string | null;
  hr_note: string | null;
  approved_by: string | null;
  approved_at: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // v5 supabase joins (only present on supabase fallback path)
  employee?: { id: string; full_name: string; email?: string; department_id?: string };
}


/**
 * HR / manager submit-on-behalf flow. Backend POST /leave/requests
 * server-stamps employee_id when not present (defaults to caller).
 * For HR submitting on behalf of someone else, employee_id must be
 * passed explicitly.
 */
export interface SubmitLeavePayload {
  employee_id?: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  reason?: string;
  days?: number;
}

/**
 * Result of a leave mutation, mirroring the app's ApiResult convention (ok/error).
 * Callers MUST check `ok` and surface `error` — never ignore the return (a failed
 * write on a legally-consequential action must never look like success).
 */
export interface LeaveMutationResult {
  ok: boolean;
  /** Human-readable failure reason when `ok` is false. */
  error?: string;
}

/** submitLeaveRequest additionally returns the new row id on success. */
export interface SubmitLeaveResult extends LeaveMutationResult {
  id?: string;
}

export async function submitLeaveRequest(payload: SubmitLeavePayload): Promise<SubmitLeaveResult> {
  const outcome = await bridgedWrite<string>(
    'leave',
    '/leave/requests',
    {
      method: 'POST',
      body: payload,
      // ⚠️ NOT IDEMPOTENT, AND THIS IS THE DECLARATION THE BRIDGE ACTS ON.
      //
      // `leave_requests` has no unique constraint but its primary key, and a leave
      // request has no natural key — same person, same dates, same type is a
      // legitimate thing to submit twice. So nothing, human or machine, can tell a
      // duplicate from a real second request after the fact.
      //
      // Declaring `false` is what makes the bridge refuse to fall back when the
      // response is lost. Changing it to `'upsert'` without a backend key behind it
      // would silently restore the duplicate. Lis's ruling 2026-08-25, option C.
      idempotent: false,
      // A create may answer with an id, with a body, or with 204 No Content. The
      // id is a bonus, never the test of whether the row exists — see the comment
      // on the automation dispatch below.
      schema: (raw) =>
        raw && typeof raw === 'object' && typeof (raw as { id?: unknown }).id === 'string'
          ? (raw as { id: string }).id
          : null,
    },
    // Supabase fallback. Returns the new id and THROWS on failure — the bridge
    // turns a throw into `{ ok: false, unconfirmed: false }`.
    async () => {
      const { data, error: dbErr } = await supabase
        .from('leave_requests')
        .insert({ ...payload, status: 'pending' })
        .select('id')
        .single();
      // supabase returns query errors WITHOUT throwing, so surface the actual
      // error rather than dropping it silently.
      if (dbErr) {
        console.error('[submitLeaveRequest] supabase insert error:', dbErr);
        throw new Error(dbErr.message);
      }
      if (!data) throw new Error('Could not submit the leave request.');
      return (data as { id: string }).id;
    },
  );

  if (!outcome.ok) {
    return { ok: false, error: outcome.error };
  }

  const id = outcome.data;

  // Engine 5 (Automation): fire any "leave.submitted" rules. Best-effort and
  // non-blocking — automation failures never affect the submit result.
  //
  // ⚠️ NO ID MEANS NO AUTOMATION, AND THAT IS THE RIGHT TRADE. A 2xx with no id in
  // the body is a COMMITTED row we cannot name. The old code treated that as a
  // failure and inserted a second row through Supabase to get an id — one
  // confirmation, two rows, a manager approving one of them and the other pending
  // forever. Skipping an automation is a smaller harm than manufacturing a
  // duplicate, and it is visible: the request is there, the rule did not run.
  if (id) {
    runAutomations('leave.submitted', { id, ...payload, status: 'pending' }).catch((e) =>
      console.warn('[automations] leave.submitted dispatch failed', e),
    );
  }

  return { ok: true, id };
}


/**
 * Approve a leave request. `stage` distinguishes manager-approve
 * (intermediate state) from hr-approve (terminal). Server enforces
 * the right role per stage.
 */
export async function approveLeaveRequest(
  requestId: string,
  stage: 'manager' | 'hr',
  note?: string,
): Promise<LeaveMutationResult> {
  // Load prior state up-front — needed both for idempotency (only deduct on the
  // real transition into 'approved') and for the deduction fields.
  const before = await _loadLeaveRequestForBalance(requestId);

  let ok = false;
  let error: string | undefined;
  if (apiConfigured()) {
    const res = await api.post(`/leave/requests/${requestId}/approve`, {
      stage,
      ...(note ? { note } : {}),
    });
    if (res.ok) ok = true; else error = res.error;
  }
  if (!ok) {
    try {
      const newStatus = stage === 'hr' ? 'approved' : 'manager_approved';
      const noteCol   = stage === 'hr' ? 'hr_note' : 'manager_note';
      const { error: dbErr } = await supabase
        .from('leave_requests')
        .update({
          status: newStatus,
          ...(note ? { [noteCol]: note } : {}),
          approved_by: undefined,  // supabase RLS handles this
        })
        .eq('id', requestId);
      if (dbErr) { console.error('[approveLeaveRequest] supabase update error:', dbErr); error = dbErr.message; }
      else { ok = true; error = undefined; }
    } catch (err) {
      console.warn('[approveLeaveRequest] supabase fallback failed', err);
      error = err instanceof Error ? err.message : 'Could not approve the request.';
    }
  }

  // Deduct ONCE — only on terminal (hr) approval, and only when this is the real
  // transition into 'approved' (a re-approve of an already-approved request is a
  // no-op, so it can't double-deduct). Non-fatal: applyBalanceDelta swallows errors.
  if (ok && stage === 'hr' && before && before.status !== 'approved') {
    const year = new Date(before.start_date).getFullYear();
    await applyBalanceDelta(before.employee_id, before.leave_type, year, +Number(before.days || 0));
  }
  return ok ? { ok: true } : { ok: false, error: error ?? 'Could not approve the request.' };
}


// ─── Leave balances (v7.57) ─────────────────────────────────────────────────


export interface LeaveBalanceRow {
  id: string;
  employee_id: string;
  year: number;
  annual_entitled: number;
  annual_used: number;
  sick_entitled: number;
  sick_used: number;
  personal_entitled: number;
  personal_used: number;
  carry_over_days: number;
  created_at: string;
  updated_at: string;
  // v5 supabase joins (only present on supabase fallback path)
  employee?: { id: string; full_name: string; email?: string; department_id?: string };
}


/**
 * Admin workspace-wide leave-balances list.
 *
 * NO LONGER EXPORTED. Its only external consumer was hr/LeaveBalancesPage.tsx,
 * deleted as dead code (the live screen is admin/LeaveBalancesPage.tsx, which
 * fetches for itself). It is NOT dead, though: `applyBalanceDelta` below calls
 * it on every terminal HR approval to find the row to deduct from — so it stays
 * as a module-internal helper rather than being removed.
 *
 * Backend: GET /leave/balances?year=&employee_id=. Admin-only; helper
 * falls through to supabase + RLS for closed-gate / non-admin paths.
 */
async function fetchAllLeaveBalances(
  filters: { year?: number; employeeId?: string } = {},
): Promise<LeaveBalanceRow[]> {
  if (apiConfigured()) {
    const params = new URLSearchParams();
    if (filters.year !== undefined) params.set('year', String(filters.year));
    if (filters.employeeId)         params.set('employee_id', filters.employeeId);
    const res = await api.get<{ items: Record<string, unknown>[] }>(
      `/leave/balances${params.toString() ? '?' + params.toString() : ''}`,
    );
    if (res.ok && res.data && Array.isArray(res.data.items)) {
      return res.data.items as unknown as LeaveBalanceRow[];
    }
  }
  try {
    let q = supabase
      .from('leave_balances')
      .select('*, employee:profiles!employee_id(id,full_name,email,department_id,department:departments!department_id(name))')
      .order('created_at', { ascending: false });
    if (filters.year !== undefined) q = q.eq('year', filters.year);
    if (filters.employeeId)         q = q.eq('employee_id', filters.employeeId);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as LeaveBalanceRow[];
  } catch (err) {
    console.warn('[fetchAllLeaveBalances] supabase fallback failed', err);
    return [];
  }
}


/**
 * Computed "used" days per leave type (annual/sick/personal).
 *
 * `ok` is false when the underlying read FAILED, in which case the three counts
 * are zeros that mean "unknown", not "none taken". Callers that render a
 * remaining balance must check it: `entitled - 0` is a confident "full
 * allowance left", which is the worst possible thing to show someone who has
 * actually booked leave. Additive so existing callers keep compiling; they just
 * keep the old behaviour until they opt in.
 */
export interface UsedByType { annual: number; sick: number; personal: number; ok: boolean; }

/**
 * B3 fix — compute "used" at READ TIME from APPROVED, non-deleted leave requests
 * for an employee in a given year (year = the leave's start_date year, matching
 * applyBalanceDelta's convention).
 *
 * This is self-healing: seed/SQL-approved leave bypasses the stored *_used
 * counters (they stay 0), so display sites overlay this computed value instead.
 * READ-ONLY — it never writes leave_balances and is independent of the
 * approve/cancel/reject mutation flow and applyBalanceDelta (those stay the
 * source of truth for the stored counters / admin editor).
 *
 * Defensive: never throws; returns zeros on any failure.
 */
export async function computeUsedFromApprovedLeave(
  employeeId: string,
  year: number,
): Promise<UsedByType> {
  const used: UsedByType = { annual: 0, sick: 0, personal: 0, ok: true };
  if (!employeeId) return { ...used, ok: false };
  try {
    // `error` was not destructured here, and the docblock's "returns zeros on
    // any failure" promise made that look deliberate. It meant a failed read
    // produced used = 0, and the caller rendered `entitled - 0` — a confident
    // "full allowance remaining" for someone who may have taken twelve days.
    const { data, error } = await supabase
      .from('leave_requests')
      .select('leave_type, days, start_date')
      .eq('employee_id', employeeId)
      .eq('status', 'approved')
      .eq('is_deleted', false);
    if (error) {
      console.warn('[computeUsedFromApprovedLeave] query failed', error);
      return { annual: 0, sick: 0, personal: 0, ok: false };
    }
    (data ?? []).forEach((r: { leave_type: string; days: number; start_date: string }) => {
      if (!r.start_date || new Date(r.start_date).getFullYear() !== year) return;
      if (r.leave_type === 'annual' || r.leave_type === 'sick' || r.leave_type === 'personal') {
        used[r.leave_type] += Number(r.days) || 0;
      }
    });
  } catch (err) {
    console.warn('[computeUsedFromApprovedLeave] failed', err);
    return { annual: 0, sick: 0, personal: 0, ok: false };
  }
  return used;
}


/** Admin create. Returns the new id, null on failure (e.g. 409 duplicate). */
export async function createLeaveBalance(payload: Record<string, unknown>): Promise<string | null> {
  if (apiConfigured()) {
    const res = await api.post<{ id: string }>('/leave/balances', payload);
    if (res.ok && res.data?.id) return res.data.id;
  }
  try {
    const { data, error } = await supabase
      .from('leave_balances')
      .insert(payload)
      .select('id')
      .single();
    if (error || !data) return null;
    return (data as { id: string }).id;
  } catch (err) {
    console.warn('[createLeaveBalance] supabase fallback failed', err);
    return null;
  }
}


/** Admin partial update. */
export async function patchLeaveBalance(
  balanceId: string,
  patch: Record<string, unknown>,
): Promise<boolean> {
  if (apiConfigured()) {
    const res = await api.patch(`/leave/balances/${balanceId}`, patch);
    if (res.ok) return true;
  }
  try {
    const { error } = await supabase
      .from('leave_balances')
      .update(patch)
      .eq('id', balanceId);
    return !error;
  } catch (err) {
    console.warn('[patchLeaveBalance] supabase fallback failed', err);
    return false;
  }
}


// ─── Balance deduction (v7.58) ──────────────────────────────────────────────

/** Maps a LeaveType to its *_used column. Types without a column don't deduct. */
const USED_COLUMN: Record<string, 'annual_used' | 'sick_used' | 'personal_used'> = {
  annual:   'annual_used',
  sick:     'sick_used',
  personal: 'personal_used',
};

/** Minimal request load — the fields the balance deduction needs + prior status. */
async function _loadLeaveRequestForBalance(
  requestId: string,
): Promise<{ employee_id: string; leave_type: string; days: number; start_date: string; status: string } | null> {
  try {
    const { data, error } = await supabase
      .from('leave_requests')
      .select('employee_id, leave_type, days, start_date, status')
      .eq('id', requestId)
      .single();
    if (error) throw error;
    return (data as { employee_id: string; leave_type: string; days: number; start_date: string; status: string } | null) ?? null;
  } catch {
    return null;
  }
}

/**
 * Apply `deltaDays` to an employee's *_used balance for a given year/type.
 * +days on terminal approval, -days when an approved request is cancelled/rejected.
 *
 * - annual/sick/personal → the matching *_used column; unpaid/maternity/paternity
 *   have no column → no-op.
 * - Creates the year's balance row with default entitlements if none exists, so a
 *   deduction never silently fails on a missing row.
 * - Clamps used at >= 0 (a reversal can never push it negative).
 * - NON-FATAL: any failure is logged and swallowed — a balance update must never
 *   block the approval/cancel/reject itself (the status change is the source of truth).
 */
async function applyBalanceDelta(
  employeeId: string,
  leaveType: string,
  year: number,
  deltaDays: number,
): Promise<void> {
  try {
    const col = USED_COLUMN[leaveType];
    if (!col) return;          // unpaid / maternity / paternity — no balance column
    if (!deltaDays) return;    // nothing to apply

    const rows = await fetchAllLeaveBalances({ year, employeeId });
    const row = rows[0];

    if (!row) {
      // No balance row for this year — create one with defaults, then apply.
      const newId = await createLeaveBalance({
        employee_id:       employeeId,
        year,
        annual_entitled:   21,
        annual_used:       0,
        sick_entitled:     10,
        sick_used:         0,
        personal_entitled: 5,
        personal_used:     0,
        carry_over_days:   0,
      });
      if (!newId) {
        console.error('[leave] applyBalanceDelta failed: could not create balance row');
        return;
      }
      await patchLeaveBalance(newId, { [col]: Math.max(0, deltaDays) });
      return;
    }

    const current = Number((row as unknown as Record<string, unknown>)[col]) || 0;
    const next = Math.max(0, current + deltaDays);
    const ok = await patchLeaveBalance(row.id, { [col]: next });
    if (!ok) console.error('[leave] applyBalanceDelta failed: patchLeaveBalance returned false for', row.id);
  } catch (err) {
    console.error('[leave] applyBalanceDelta failed:', err instanceof Error ? err.message : err);
  }
}


/** Reject a leave request. Same stage semantics as approve. */
export async function rejectLeaveRequest(
  requestId: string,
  stage: 'manager' | 'hr',
  note?: string,
): Promise<LeaveMutationResult> {
  // Load prior state — we only restore the balance if this request had already
  // been (terminally) approved, i.e. a deduction had happened.
  const before = await _loadLeaveRequestForBalance(requestId);

  let ok = false;
  let error: string | undefined;
  if (apiConfigured()) {
    const res = await api.post(`/leave/requests/${requestId}/reject`, {
      stage,
      ...(note ? { note } : {}),
    });
    if (res.ok) ok = true; else error = res.error;
  }
  if (!ok) {
    try {
      const noteCol = stage === 'hr' ? 'hr_note' : 'manager_note';
      const { error: dbErr } = await supabase
        .from('leave_requests')
        .update({
          status: 'rejected',
          ...(note ? { [noteCol]: note } : {}),
        })
        .eq('id', requestId);
      if (dbErr) { console.error('[rejectLeaveRequest] supabase update error:', dbErr); error = dbErr.message; }
      else { ok = true; error = undefined; }
    } catch (err) {
      console.warn('[rejectLeaveRequest] supabase fallback failed', err);
      error = err instanceof Error ? err.message : 'Could not reject the request.';
    }
  }

  // Reverse ONCE — only when rejecting a request that was previously 'approved'
  // (so a deduction existed). Rejecting a still-pending request restores nothing.
  if (ok && before && before.status === 'approved') {
    const year = new Date(before.start_date).getFullYear();
    await applyBalanceDelta(before.employee_id, before.leave_type, year, -Number(before.days || 0));
  }
  return ok ? { ok: true } : { ok: false, error: error ?? 'Could not reject the request.' };
}


/**
 * Cancel a leave request (status → 'cancelled'). If the request had already
 * been (terminally) approved, the deducted balance is restored.
 *
 * NOTE (v7.58): leave.ts had no cancel helper before — the employee-side cancel
 * in MyLeavePage soft-deletes the row directly. This adds the canonical
 * cancel-with-reversal flow in the service layer; wiring the UI cancel button to
 * call it (instead of the raw soft-delete) is a follow-up.
 */
export async function cancelLeaveRequest(requestId: string): Promise<LeaveMutationResult> {
  const before = await _loadLeaveRequestForBalance(requestId);

  let ok = false;
  let error: string | undefined;
  if (apiConfigured()) {
    const res = await api.patch(`/leave/requests/${requestId}/cancel`);
    if (res.ok) ok = true; else error = res.error;
  }
  if (!ok) {
    try {
      const { error: dbErr } = await supabase
        .from('leave_requests')
        .update({ status: 'cancelled' })
        .eq('id', requestId);
      if (dbErr) { console.error('[cancelLeaveRequest] supabase update error:', dbErr); error = dbErr.message; }
      else { ok = true; error = undefined; }
    } catch (err) {
      console.warn('[cancelLeaveRequest] supabase fallback failed', err);
      error = err instanceof Error ? err.message : 'Could not cancel the request.';
    }
  }

  // Reverse ONCE — only if cancelling a previously-approved request.
  if (ok && before && before.status === 'approved') {
    const year = new Date(before.start_date).getFullYear();
    await applyBalanceDelta(before.employee_id, before.leave_type, year, -Number(before.days || 0));
  }
  return ok ? { ok: true } : { ok: false, error: error ?? 'Could not cancel the request.' };
}
